import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Check } from "lucide-react";
import Button from "@/components/site/Button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const EMPTY = {
    naam: "",
    email: "",
    vraag: "",
    nieuwsbrief_opt_in: false,
    privacy_akkoord: false,
    website: "",
};

export default function QAWidget() {
    const { pathname } = useLocation();
    const reduced = useReducedMotion();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    const openedAt = useRef(0);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);

    const hiddenOnPage = pathname === "/gratis-qa";

    // One-time, engagement-based attention hint (~50% scroll depth).
    // Toggles the pulse class directly on the button(s) so it is reliable
    // regardless of React re-render timing.
    useEffect(() => {
        if (hiddenOnPage || reduced) return;
        if (localStorage.getItem("hds_qa_hinted")) return;
        const onScroll = () => {
            const max =
                document.documentElement.scrollHeight - window.innerHeight;
            const depth = max > 0 ? window.scrollY / max : 0;
            if (depth >= 0.5) {
                localStorage.setItem("hds_qa_hinted", "1");
                window.removeEventListener("scroll", onScroll);
                document
                    .querySelectorAll("[data-qa-hint-target]")
                    .forEach((el) => {
                        el.classList.add("qa-hint");
                        setTimeout(() => el.classList.remove("qa-hint"), 1200);
                    });
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [hiddenOnPage, reduced]);

    const close = useCallback(() => {
        setOpen(false);
        buttonRef.current?.focus();
    }, []);

    // Esc to close + focus trap
    useEffect(() => {
        if (!open) return;
        openedAt.current = Date.now();
        const onKey = (e) => {
            if (e.key === "Escape") {
                close();
                return;
            }
            if (e.key === "Tab" && panelRef.current) {
                const nodes = panelRef.current.querySelectorAll(
                    'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
                );
                const focusable = Array.from(nodes).filter((n) => !n.disabled && n.offsetParent !== null);
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        // focus first field
        const t = setTimeout(() => {
            panelRef.current?.querySelector("input, button")?.focus();
        }, 60);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
            clearTimeout(t);
        };
    }, [open, close]);

    const validate = () => {
        const e = {};
        if (!form.naam.trim()) e.naam = "Vul je naam in.";
        if (!emailOk(form.email)) e.email = "Vul een geldig e-mailadres in.";
        if (!form.privacy_akkoord) e.privacy_akkoord = "Je moet akkoord gaan om je aan te melden.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        setServerError("");
        try {
            await axios.post(`${API}/qa-signups`, {
                ...form,
                elapsed_ms: Date.now() - openedAt.current,
            });
            setDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setServerError(typeof d === "string" ? d : "Er ging iets mis. Probeer het opnieuw.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setForm(EMPTY);
        setErrors({});
        setDone(false);
        setServerError("");
    };

    if (hiddenOnPage) return null;

    const slide = reduced
        ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
        : {
              initial: { x: "100%" },
              animate: { x: 0 },
              exit: { x: "100%" },
              transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
          };

    return (
        <>
            {/* Floating button */}
            {!open && (
                <>
                    {/* Desktop side-tab */}
                    <button
                        ref={buttonRef}
                        type="button"
                        onClick={() => { reset(); setOpen(true); }}
                        data-testid="qa-widget-button"
                        data-qa-hint-target
                        aria-label="Open gratis Q&A aanmeldformulier"
                        className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-30 items-center gap-2 bg-[var(--hds-clay)] hover:bg-[var(--hds-clay-dark)] text-[var(--hds-card)] py-4 px-3 rounded-l-[14px] shadow-[0_10px_30px_rgba(44,39,34,0.18)] transition-[background,transform] duration-400 hover:-translate-x-1"
                        style={{ writingMode: "vertical-rl" }}
                    >
                        <span className="rotate-180 text-[13.5px] tracking-[0.14em]">Gratis Q&A</span>
                    </button>
                    {/* Mobile pill */}
                    <button
                        type="button"
                        onClick={() => { reset(); setOpen(true); }}
                        data-testid="qa-widget-button-mobile"
                        data-qa-hint-target
                        aria-label="Open gratis Q&A aanmeldformulier"
                        className="md:hidden fixed right-4 bottom-4 z-30 inline-flex items-center gap-2 bg-[var(--hds-clay)] text-[var(--hds-card)] rounded-full px-5 py-3 text-[13.5px] tracking-wide shadow-[0_10px_30px_rgba(44,39,34,0.22)]"
                    >
                        Gratis Q&A
                    </button>
                </>
            )}

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            key="qa-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={close}
                            data-testid="qa-widget-overlay"
                            className="fixed inset-0 z-[55] bg-[#2C2722]/45 backdrop-blur-[2px]"
                        />
                        <motion.div
                            key="qa-panel"
                            ref={panelRef}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Gratis Q&A aanmelding"
                            data-testid="qa-widget-panel"
                            {...slide}
                            className="fixed z-[56] bg-[var(--hds-sand)] shadow-2xl overflow-y-auto
                                right-0 top-0 bottom-0 w-full md:w-[400px]
                                max-md:top-auto max-md:h-[92vh] max-md:rounded-t-[20px]"
                        >
                            <div className="sticky top-0 flex items-center justify-between px-6 py-4 bg-[var(--hds-sand)]/95 backdrop-blur border-b border-[var(--hds-line)]">
                                <span className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay)]">
                                    Gratis Q&A
                                </span>
                                <button
                                    type="button"
                                    onClick={close}
                                    data-testid="qa-widget-close"
                                    aria-label="Sluit paneel"
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--hds-line)] hover:bg-[var(--hds-card)] transition-colors duration-300"
                                >
                                    <X size={17} strokeWidth={1.5} />
                                </button>
                            </div>

                            <div className="px-6 py-7">
                                {done ? (
                                    <div data-testid="qa-widget-confirmation">
                                        <div className="w-14 h-14 rounded-full bg-[var(--hds-green)] flex items-center justify-center text-[var(--hds-card)] mb-6">
                                            <Check size={26} strokeWidth={1.5} />
                                        </div>
                                        <h2 className="font-[Fraunces] text-[30px] leading-[1.05]" style={{ fontWeight: 300 }}>
                                            Je aanmelding is{" "}
                                            <em className="italic text-[var(--hds-clay)]" style={{ fontWeight: 300 }}>binnen</em>
                                        </h2>
                                        <p className="mt-4 text-[15.5px] leading-[1.6] text-[var(--hds-ink)]/80">
                                            Je ontvangt de Google Meet-link per e-mail.
                                        </p>
                                        <div className="mt-7">
                                            <Button variant="secondary" onClick={close} data-testid="qa-widget-done-close">
                                                Sluiten
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="font-[Fraunces] text-[27px] leading-[1.1] tracking-[-0.01em]" style={{ fontWeight: 300 }}>
                                            Gratis Q&A over{" "}
                                            <em className="italic text-[var(--hds-clay)]" style={{ fontWeight: 300 }}>Human Design</em>
                                        </h2>
                                        <p className="mt-3 text-[15px] leading-[1.6] text-[var(--hds-ink)]/78">
                                            Elke twee weken een gratis online sessie via Google Meet. Meld je aan, stel je vraag en kijk vrijblijvend wat Human Design is.
                                        </p>

                                        {/* honeypot */}
                                        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden data-testid="qa-widget-website" />

                                        <div className="mt-6 space-y-4">
                                            <WField label="Naam" name="naam" value={form.naam} error={errors.naam} form={form} onChange={setForm} testid="qa-widget-naam" />
                                            <WField label="E-mailadres" name="email" type="email" value={form.email} error={errors.email} form={form} onChange={setForm} testid="qa-widget-email" />
                                            <div>
                                                <label className="block text-[12.5px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">Je vraag (optioneel)</label>
                                                <textarea data-testid="qa-widget-vraag" rows={2} value={form.vraag} onChange={(e) => setForm({ ...form, vraag: e.target.value })} className="w-full rounded-[12px] bg-[var(--hds-card)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[15px] transition-colors duration-300" />
                                            </div>

                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input type="checkbox" data-testid="qa-widget-privacy" checked={form.privacy_akkoord} onChange={(e) => setForm({ ...form, privacy_akkoord: e.target.checked })} className="mt-0.5 w-5 h-5 accent-[var(--hds-clay)] shrink-0" />
                                                <span className="text-[13.5px] leading-[1.5] text-[var(--hds-ink)]/80">
                                                    Ik ga akkoord met de{" "}
                                                    <Link to="/privacy" className="text-[var(--hds-clay)] underline underline-offset-2" onClick={close}>privacyverklaring</Link>.
                                                </span>
                                            </label>
                                            {errors.privacy_akkoord && <p className="text-[13px] text-[var(--hds-clay-dark)]" data-testid="qa-widget-error-privacy">{errors.privacy_akkoord}</p>}

                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input type="checkbox" data-testid="qa-widget-nieuwsbrief" checked={form.nieuwsbrief_opt_in} onChange={(e) => setForm({ ...form, nieuwsbrief_opt_in: e.target.checked })} className="mt-0.5 w-5 h-5 accent-[var(--hds-clay)] shrink-0" />
                                                <span className="text-[13.5px] leading-[1.5] text-[var(--hds-ink)]/70">Houd me op de hoogte van nieuwe sessies en tips.</span>
                                            </label>

                                            {serverError && <p className="text-[13.5px] text-[var(--hds-clay-dark)]" data-testid="qa-widget-server-error">{serverError}</p>}

                                            <Button variant="primary" icon="arrow" onClick={submit} disabled={submitting} data-testid="qa-widget-submit">
                                                {submitting ? "Bezig..." : "Meld me aan"}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}

const WField = ({ label, name, type = "text", value, error, form, onChange, testid }) => (
    <div>
        <label className="block text-[12.5px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">{label}</label>
        <input
            type={type}
            data-testid={testid}
            value={value}
            onChange={(e) => onChange({ ...form, [name]: e.target.value })}
            className={`w-full rounded-[12px] bg-[var(--hds-card)] border outline-none px-4 py-2.5 text-[15px] transition-colors duration-300 ${error ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"}`}
        />
        {error && <p className="mt-1.5 text-[13px] text-[var(--hds-clay-dark)]" data-testid={`${testid}-error`}>{error}</p>}
    </div>
);
